/**
 * 管理画面用レイアウトコンポーネント
 */

import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Users,
  UserCircle,
  FileText,
  FileCheck,
  Brain,
  MessageSquare,
  Award,
  History,
  BookOpen,
  Mic,
  Menu,
  X,
  LogOut,
  BookMarked,
  Home,
  HardDrive,
  Layers,
  Library,
  ChevronDown,
  ScrollText,
  Trophy,
  QrCode,
} from 'lucide-react';
import { Button } from '../ui/button';
import { AuthService } from '../../lib/auth';
import { cn } from '../../lib/utils';
import type { StaffRole } from '../../types';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';

interface NavItem {
  label: string;
  href: string;
  icon: ReactNode;
}

function isActivePath(location: string, href: string): boolean {
  if (href === '/admin') return location === '/admin';
  if (href === '/teacher') return location === '/teacher';
  if (href === DEMO_TEACHER_HOME) return location === DEMO_TEACHER_HOME || location.startsWith(`${DEMO_TEACHER_HOME}/classes/`);
  return location === href || location.startsWith(`${href}/`);
}

/** 教員画面の公開デモ（/demo/teacher）。メニューからは本物の画面へ出ない */
export const DEMO_TEACHER_HOME = '/demo/teacher';
const DEMO_TEACHER_NAV_ITEMS: NavItem[] = [
  { label: 'ダッシュボード', href: DEMO_TEACHER_HOME, icon: <LayoutDashboard className="w-5 h-5" /> },
  { label: 'ランキング', href: `${DEMO_TEACHER_HOME}/rankings`, icon: <Trophy className="w-5 h-5" /> },
  { label: '担当学生', href: `${DEMO_TEACHER_HOME}/students`, icon: <Users className="w-5 h-5" /> },
  { label: '面接評価一覧', href: `${DEMO_TEACHER_HOME}/scores`, icon: <Award className="w-5 h-5" /> },
];

/** ダッシュボード・ユーザー（単体リンク） */
const TOP_NAV_ITEMS: NavItem[] = [
  { label: 'ダッシュボード', href: '/admin', icon: <LayoutDashboard className="w-5 h-5" /> },
  { label: 'ランキング', href: '/teacher/rankings', icon: <Trophy className="w-5 h-5" /> },
  { label: 'ユーザー管理', href: '/admin/users', icon: <Users className="w-5 h-5" /> },
  { label: 'ゲスト入場', href: '/admin/guest-passes', icon: <QrCode className="w-5 h-5" /> },
];

/** ユーザー管理の直下（スコア管理） */
const AFTER_USER_NAV_ITEMS: NavItem[] = [
  { label: 'スコア管理', href: '/admin/scores', icon: <Award className="w-5 h-5" /> },
];

/** 知識管理 配下（LLM・プロンプト） */
const KNOWLEDGE_ITEMS: NavItem[] = [
  { label: 'LLM設定', href: '/admin/llms', icon: <Brain className="w-5 h-5" /> },
  { label: 'プロンプト管理', href: '/admin/prompts', icon: <MessageSquare className="w-5 h-5" /> },
];

/** アバター設定 配下 */
const AVATAR_SETTINGS_ITEMS: NavItem[] = [
  { label: 'アバター管理', href: '/admin/avatars', icon: <UserCircle className="w-5 h-5" /> },
  { label: '健康心理条件管理', href: '/admin/conditions', icon: <FileText className="w-5 h-5" /> },
  { label: '問診票管理', href: '/admin/questionnaires', icon: <FileText className="w-5 h-5" /> },
  { label: '講座シナリオ設定', href: '/admin/course-scenarios', icon: <BookOpen className="w-5 h-5" /> },
  { label: '課題文管理', href: '/admin/instructions', icon: <FileCheck className="w-5 h-5" /> },
  { label: '課題文作成', href: '/admin/instruction-builder', icon: <Layers className="w-5 h-5" /> },
];

/** 辞書 配下 */
const DICTIONARY_ITEMS: NavItem[] = [
  { label: 'TTS読み方辞書', href: '/admin/tts-pronunciation-dictionary', icon: <BookOpen className="w-5 h-5" /> },
  { label: 'STT設定', href: '/admin/stt-settings', icon: <Mic className="w-5 h-5" /> },
  { label: '専門用語管理', href: '/admin/technical-terms', icon: <BookMarked className="w-5 h-5" /> },
];

/** その他の単体リンク */
const REST_NAV_ITEMS: NavItem[] = [
  { label: 'TTSキャッシュ', href: '/admin/performance/tts-cache', icon: <HardDrive className="w-5 h-5" /> },
  { label: '操作履歴', href: '/admin/histories', icon: <History className="w-5 h-5" /> },
];

const ALL_FLAT_HREFS = [
  ...TOP_NAV_ITEMS.map((i) => i.href),
  ...AFTER_USER_NAV_ITEMS.map((i) => i.href),
  ...AVATAR_SETTINGS_ITEMS.map((i) => i.href),
  ...DICTIONARY_ITEMS.map((i) => i.href),
  ...KNOWLEDGE_ITEMS.map((i) => i.href),
  ...REST_NAV_ITEMS.map((i) => i.href),
];

const NAV_HREFS_BY_ROLE: Record<StaffRole, string[]> = {
  admin: ALL_FLAT_HREFS.filter((href) => !href.startsWith('/teacher/')),
  educator: [
    '/admin',
    '/teacher/rankings',
    '/admin/users',
    '/admin/guest-passes',
    '/admin/scores',
    ...AVATAR_SETTINGS_ITEMS
      .filter((item) => item.href !== '/admin/conditions')
      .map((i) => i.href),
    ...DICTIONARY_ITEMS.map((i) => i.href),
  ],
  student: ['/admin', '/admin/scores'],
};

function allowedHrefSet(role: StaffRole | undefined): Set<string> {
  const key: StaffRole = role ?? 'student';
  return new Set(NAV_HREFS_BY_ROLE[key]);
}

function filterByRole(items: NavItem[], allowed: Set<string>): NavItem[] {
  return items.filter((item) => allowed.has(item.href));
}

function toEducatorHref(href: string): string {
  if (href === '/admin') return '/teacher';
  if (href === '/admin/users') return '/teacher/students';
  if (href.startsWith('/admin/')) return `/teacher/${href.slice('/admin/'.length)}`;
  return href;
}

function withEducatorRouteAliases(items: NavItem[], role: StaffRole | undefined): NavItem[] {
  if (role !== 'educator') return items;
  return items.map((item) => {
    const href = toEducatorHref(item.href);
    return {
      ...item,
      href,
      label:
        item.href === '/admin/users'
          ? '担当学生'
          : item.href === '/admin/scores'
            ? '面接評価一覧'
            : item.label,
    };
  });
}

interface AdminLayoutProps {
  children: ReactNode;
  /** 教員画面の公開デモ。ログインユーザーを取得せず、デモの4画面だけをメニューに出す */
  demo?: boolean;
}

export default function AdminLayout({ children, demo = false }: AdminLayoutProps) {
  const [location] = useLocation();
  const isTeacherWorkspace = demo || location.startsWith('/teacher');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(true);
  const [avatarGroupOpen, setAvatarGroupOpen] = useState(true);
  const [dictionaryGroupOpen, setDictionaryGroupOpen] = useState(true);
  const [knowledgeGroupOpen, setKnowledgeGroupOpen] = useState(true);
  const authService = AuthService.getInstance();

  const { data: me } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => authService.getCurrentUser(),
    staleTime: 60 * 1000,
    enabled: !demo,
  });

  const role = me?.role as StaffRole | undefined;
  const layoutRole: StaffRole | undefined = isTeacherWorkspace ? 'educator' : role;
  const allowed = useMemo(() => allowedHrefSet(layoutRole), [layoutRole]);
  const headerTitle =
    layoutRole === 'student' ? 'マイページ' : layoutRole === 'educator' ? '教員ページ' : '管理画面';

  const topNav = useMemo(
    () => demo
      ? DEMO_TEACHER_NAV_ITEMS
      : withEducatorRouteAliases(filterByRole(TOP_NAV_ITEMS, allowed), layoutRole),
    [allowed, layoutRole, demo]
  );
  const afterUserNav = useMemo(
    () => demo ? [] : withEducatorRouteAliases(filterByRole(AFTER_USER_NAV_ITEMS, allowed), layoutRole),
    [allowed, layoutRole, demo]
  );
  const avatarChildren = useMemo(
    () => demo ? [] : withEducatorRouteAliases(filterByRole(AVATAR_SETTINGS_ITEMS, allowed), layoutRole),
    [allowed, layoutRole, demo]
  );
  const dictionaryChildren = useMemo(
    () => demo ? [] : withEducatorRouteAliases(filterByRole(DICTIONARY_ITEMS, allowed), layoutRole),
    [allowed, layoutRole, demo]
  );
  const knowledgeChildren = useMemo(
    () => demo ? [] : withEducatorRouteAliases(filterByRole(KNOWLEDGE_ITEMS, allowed), layoutRole),
    [allowed, layoutRole, demo]
  );
  const restNav = useMemo(
    () => demo ? [] : withEducatorRouteAliases(filterByRole(REST_NAV_ITEMS, allowed), layoutRole),
    [allowed, layoutRole, demo]
  );

  const avatarSectionHasActive = useMemo(
    () => avatarChildren.some((i) => isActivePath(location, i.href)),
    [location, avatarChildren]
  );
  const dictionarySectionHasActive = useMemo(
    () => dictionaryChildren.some((i) => isActivePath(location, i.href)),
    [location, dictionaryChildren]
  );
  const knowledgeSectionHasActive = useMemo(
    () => knowledgeChildren.some((i) => isActivePath(location, i.href)),
    [location, knowledgeChildren]
  );

  useEffect(() => {
    if (avatarSectionHasActive) setAvatarGroupOpen(true);
  }, [avatarSectionHasActive]);

  useEffect(() => {
    if (dictionarySectionHasActive) setDictionaryGroupOpen(true);
  }, [dictionarySectionHasActive]);

  useEffect(() => {
    if (knowledgeSectionHasActive) setKnowledgeGroupOpen(true);
  }, [knowledgeSectionHasActive]);

  const handleLogout = async () => {
    await authService.logout();
  };

  const renderNavLink = (item: NavItem, options?: { nested?: boolean }) => {
    const nested = options?.nested ?? false;
    const active = isActivePath(location, item.href);
    return (
      <Link key={item.href} href={item.href}>
        <a
          href={item.href}
          className={cn(
            'relative flex items-center space-x-3 rounded-lg text-sm font-medium transition-colors',
            nested ? 'px-3 py-2 pl-9' : 'px-3 py-2',
            active
              ? 'bg-cyan-100 text-cyan-900 font-bold before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-1 before:rounded-r-full before:bg-cyan-700'
              : 'text-gray-700 hover:bg-gray-100'
          )}
          onClick={() => setSidebarOpen(false)}
        >
          {item.icon}
          <span>{item.label}</span>
        </a>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* モバイル用ヘッダー */}
      <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <h1 className="text-lg font-semibold text-gray-900">{headerTitle}</h1>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      <div className="flex">
        {/* サイドバー */}
        <aside
          className={cn(
            "fixed inset-y-0 left-0 z-50 overflow-hidden bg-white border-r border-gray-200 transform transition-all duration-200 ease-in-out lg:sticky lg:top-0 lg:h-screen lg:flex-shrink-0",
            sidebarOpen ? "translate-x-0 w-64" : "-translate-x-full w-64",
            desktopSidebarOpen ? "lg:w-64 lg:translate-x-0 lg:border-r" : "lg:w-0 lg:-translate-x-full lg:border-r-0"
          )}
        >
          <div className="flex flex-col h-full">
            {/* TOPへ（サイドバー最上位） */}
            <div className="shrink-0 px-4 pt-3 pb-2 border-b border-gray-200">
              <Link href={demo ? DEMO_TEACHER_HOME : "/select-filter"}>
                <a
                  href={demo ? DEMO_TEACHER_HOME : "/select-filter"}
                  className="flex items-center w-full px-3 py-2 text-sm font-bold text-cyan-700 hover:bg-cyan-50 rounded-lg"
                  onClick={() => setSidebarOpen(false)}
                >
                  <Home className="w-4 h-4 mr-2 shrink-0" />
                  TOPへ
                </a>
              </Link>
            </div>

            {/* ロゴ・タイトル */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">{headerTitle}</h2>
              <p className="text-sm text-gray-500 mt-1">デンタルアバターシステム</p>
              {demo && (
                <span className="mt-2 inline-flex rounded-full bg-cyan-50 px-2.5 py-1 text-xs font-bold text-cyan-800">
                  公開デモ・閲覧専用
                </span>
              )}
            </div>

            {/* ナビゲーション */}
            <nav className="flex-1 overflow-y-auto px-4 py-4">
              <ul className="space-y-1">
                {topNav.map((item) => (
                  <li key={item.href}>{renderNavLink(item)}</li>
                ))}

                {afterUserNav.map((item) => (
                  <li key={item.href}>{renderNavLink(item)}</li>
                ))}

                {avatarChildren.length > 0 && (
                  <li className="pt-1">
                    <Collapsible open={avatarGroupOpen} onOpenChange={setAvatarGroupOpen}>
                      <CollapsibleTrigger asChild>
                        <button
                          type="button"
                          className={cn(
                            'flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm font-semibold text-gray-800 hover:bg-gray-100',
                            avatarSectionHasActive && 'text-medical-blue'
                          )}
                        >
                          <span className="flex items-center gap-2">
                            <Layers className="w-5 h-5 shrink-0" />
                            アバター設定
                          </span>
                          <ChevronDown
                            className={cn(
                              'h-4 w-4 shrink-0 transition-transform',
                              avatarGroupOpen && 'rotate-180'
                            )}
                          />
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <ul className="mt-1 space-y-0.5 border-l border-gray-200 ml-3 pl-1">
                          {avatarChildren.map((item) => (
                            <li key={item.href}>{renderNavLink(item, { nested: true })}</li>
                          ))}
                        </ul>
                      </CollapsibleContent>
                    </Collapsible>
                  </li>
                )}

                {dictionaryChildren.length > 0 && (
                  <li className="pt-1">
                    <Collapsible open={dictionaryGroupOpen} onOpenChange={setDictionaryGroupOpen}>
                      <CollapsibleTrigger asChild>
                        <button
                          type="button"
                          className={cn(
                            'flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm font-semibold text-gray-800 hover:bg-gray-100',
                            dictionarySectionHasActive && 'text-medical-blue'
                          )}
                        >
                          <span className="flex items-center gap-2">
                            <Library className="w-5 h-5 shrink-0" />
                            辞書
                          </span>
                          <ChevronDown
                            className={cn(
                              'h-4 w-4 shrink-0 transition-transform',
                              dictionaryGroupOpen && 'rotate-180'
                            )}
                          />
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <ul className="mt-1 space-y-0.5 border-l border-gray-200 ml-3 pl-1">
                          {dictionaryChildren.map((item) => (
                            <li key={item.href}>{renderNavLink(item, { nested: true })}</li>
                          ))}
                        </ul>
                      </CollapsibleContent>
                    </Collapsible>
                  </li>
                )}

                {knowledgeChildren.length > 0 && (
                  <li className="pt-1">
                    <Collapsible open={knowledgeGroupOpen} onOpenChange={setKnowledgeGroupOpen}>
                      <CollapsibleTrigger asChild>
                        <button
                          type="button"
                          className={cn(
                            'flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm font-semibold text-gray-800 hover:bg-gray-100',
                            knowledgeSectionHasActive && 'text-medical-blue'
                          )}
                        >
                          <span className="flex items-center gap-2">
                            <ScrollText className="w-5 h-5 shrink-0" />
                            知識管理
                          </span>
                          <ChevronDown
                            className={cn(
                              'h-4 w-4 shrink-0 transition-transform',
                              knowledgeGroupOpen && 'rotate-180'
                            )}
                          />
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <ul className="mt-1 space-y-0.5 border-l border-gray-200 ml-3 pl-1">
                          {knowledgeChildren.map((item) => (
                            <li key={item.href}>{renderNavLink(item, { nested: true })}</li>
                          ))}
                        </ul>
                      </CollapsibleContent>
                    </Collapsible>
                  </li>
                )}

                {restNav.map((item) => (
                  <li key={item.href} className="pt-0.5">
                    {renderNavLink(item)}
                  </li>
                ))}
              </ul>
            </nav>

            {/* ログアウト（デモでは出さない） */}
            {demo ? (
              <div className="shrink-0 px-6 py-4 border-t border-gray-200 text-xs leading-5 text-gray-500">
                負荷テスト用の参加者データで表示しています。操作しても本物のデータは変わりません。
              </div>
            ) : (
            <div className="shrink-0 px-4 py-4 border-t border-gray-200">
              <Button
                variant="outline"
                className="w-full justify-start text-cyan-700 border-cyan-200 hover:bg-cyan-50 hover:text-cyan-800"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                ログアウト
              </Button>
            </div>
            )}
          </div>
        </aside>

        {/* オーバーレイ（モバイル用） */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* メインコンテンツ */}
        <main className="flex-1 lg:ml-0">
          <div className="hidden lg:flex items-center gap-2 border-b border-gray-200 bg-white px-6 py-3">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setDesktopSidebarOpen((prev) => !prev)}
              aria-label={desktopSidebarOpen ? '管理メニューを閉じる' : '管理メニューを開く'}
              title={desktopSidebarOpen ? '管理メニューを閉じる' : '管理メニューを開く'}
            >
              <Menu className="w-4 h-4" />
            </Button>
          </div>
          <div className="p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
